import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AccountUnit } from '../../../../models/api/alerts/account-unit';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { AccountUnitItem } from '../../../../models/api/reports/location-filter';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { InputComponent } from '../../../core/controls/input.component';
import { RadioButtonComponent } from '../../../core/controls/radio-button.component';
import { DialogComponent } from '../../../core/panels/dialog.component';
import { LoadingPanelComponent } from '../../../core/panels/loading-panel.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { AccountsComponent } from '../../../core/controls/account-selector/accounts.component';
import { HelpIconComponent } from '../../../core/labels/help-icon.component';

@Component({
  selector: 'app-account-unit-criteria',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    InputComponent,
    RadioButtonComponent,
    DialogComponent,
    LoadingPanelComponent,
    ActionRowComponent,
    AccountsComponent,
    HelpIconComponent,
  ],
  template: `
    <div class="tw-flex tw-flex-col tw-gap-4">
      <app-action-row>
        <app-button
          left1
          label="Add account or unit"
          emphasis="Low"
          icon="fa-light fa-plus"
          (click)="openAccountSearchDialog()"
        />
        <div right1 class="tw-flex tw-items-center">
          <app-radio-button
            [control]="includeTypeControl"
            screenReaderLabel="Selection type"
            optionLabel="name"
            optionValue="code"
            [options]="includeOptions"
            [showHelpText]="false"
            [vertical]="false"
          />
        </div>
      </app-action-row>

      <app-accounts
        [items]="selectedAccounts"
        [showDeleteColumn]="true"
        recordLabel="accounts or unit"
        emptyMessage="Click add account or unit"
        (itemsChanged)="onAccountsChanged($event)"
      />
    </div>

    <app-dialog
      #accountSearchDialog
      [visible]="accountDialogVisible"
      [closable]="false"
      label="Add accounts and units"
      [width]="'900px'"
      [autoFocus]="false"
      [focusOnShow]="false"
    >
      <ng-template #content>
        <div class="tw-pb-2">
          <div class="tw-flex tw-items-center">
            <div class="tw-basis-1/4">
              <div
                class="tw-font-bold tw-pr-1 tw-flex tw-items-center tw-gap-2"
              >
                Search by level
                <app-help-icon
                  [helpText]="outputHelpText"
                  ariaLabel="Search by level information"
                  size="20px"
                />
              </div>
            </div>
            <div class="tw-basis-3/4 tw-min-w-[200px]">
              <app-radio-button
                [control]="accountSearchForm.controls.acctLevel"
                screenReaderLabel="Search by level"
                optionLabel="name"
                optionValue="code"
                [options]="acctLevelOptions"
                [showHelpText]="false"
                [vertical]="false"
              />
            </div>
          </div>
        </div>

        <form [formGroup]="accountSearchForm" class="tw-pt-2">
          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
            <app-input
              [control]="accountSearchForm.controls.accountSearch"
              label="Account Number/Name"
            />
            <app-input
              [control]="accountSearchForm.controls.unitSearch"
              label="Unit Number/Name"
              [disabled]="
                accountSearchForm.controls.acctLevel.value === 'account'
              "
            />
          </div>
        </form>

        <form [formGroup]="accountSearchForm" class="tw-pt-4">
          <div
            class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4"
          >
            <app-input
              [control]="accountSearchForm.controls.address"
              label="Address"
            />
            <app-input
              [control]="accountSearchForm.controls.city"
              label="City"
            />
            <app-input
              [control]="accountSearchForm.controls.state"
              label="State"
            />
            <app-input
              [control]="accountSearchForm.controls.zip"
              label="Zip Code"
            />
          </div>
        </form>

        <app-action-row>
          <app-button
            left1
            type="submit"
            label="Search"
            [loading]="searchingAccounts"
            (click)="searchAccounts()"
          />
          <app-button
            left2
            emphasis="Low"
            label="Clear"
            (click)="clearAccountSearch()"
          />
        </app-action-row>

        <app-loading-panel
          loadingStyle="Box"
          [state]="searchingAccounts ? 'Loading' : 'Loaded'"
        >
          <ng-template #loaded>
            <app-accounts
              #searchAccountsTable
              [items]="accountSearchResults"
              [showDeleteColumn]="false"
              paginatorDropdownAppendTo="self"
              (selected)="onAccountSearchSelectionChanged($event)"
            />
          </ng-template>
        </app-loading-panel>

        <app-action-row>
          <app-button
            [disabled]="!hasAccountSearchSelection"
            [badgeLabel]="accountSearchSelectionCount || null"
            right1
            label="Add"
            icon="fa-light fa-plus"
            (click)="addSelectedAccounts()"
          />
          <app-button
            right2
            emphasis="Low"
            label="Close"
            (click)="closeAccountDialog()"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>
  `,
  styles: ``,
})
export class AccountUnitCriteriaComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input() contractNumber: number;
  @Input() includeType: 'include' | 'exclude' = 'include';
  @Input() initialCriteria: FilterCriteria[] = [];
  @Output() includeTypeChange = new EventEmitter<'include' | 'exclude'>();
  @Output() selectionChange = new EventEmitter<void>();

  includeTypeControl = new FormControl<string>('include');
  includeOptions = [
    { name: 'Include', code: 'include' },
    { name: 'Exclude', code: 'exclude' },
  ];

  acctLevelOptions = [
    { name: 'Unit Level', code: 'unit' },
    { name: 'Account Level', code: 'account' },
  ];

  selectedAccounts: AccountUnit[] = [];
  accountSearchResults: AccountUnit[] = [];
  accountDialogVisible = false;
  searchingAccounts = false;

  outputHelpText: string =
    'Unit level searches and filters are limited to the selected unit(s). ' +
    'Account level searches return a listing of accounts; account filters include all units in the selected account(s).';

  searchLevelPreference = 'unit';

  @ViewChild('searchAccountsTable') searchAccountsTable: AccountsComponent;
  hasAccountSearchSelection = false;
  accountSearchSelectionCount = 0;

  accountSearchForm = new FormGroup({
    accountSearch: new FormControl<string>(null),
    unitSearch: new FormControl<string>(null),
    address: new FormControl<string>(null),
    city: new FormControl<string>(null),
    state: new FormControl<string>(null),
    zip: new FormControl<string>(null),
    acctLevel: new FormControl<string>('unit'),
  });

  async ngOnInit() {
    this.includeTypeControl.setValue(this.includeType, { emitEvent: false });
    this.dispose.subs = this.includeTypeControl.valueChanges.subscribe(
      (value) => {
        if (value) {
          this.includeTypeChange.emit(value as 'include' | 'exclude');
        }
      },
    );
    this.loadSearchLevelPreference();

    if (this.initialCriteria?.length) {
      await this.setAccountUnitsFromCriteria(this.initialCriteria);
    }
  }

  private async setAccountUnitsFromCriteria(criteria: FilterCriteria[]) {
    const allValues: string[] = criteria.flatMap((c) => c.values);
    if (!allValues.length || this.contractNumber <= 0) return;

    const results = await this.api.filtersCriteria.getAccountUnitDetails(
      this.contractNumber,
      allValues,
    );

    this.selectedAccounts = results.map((r: AccountUnitItem) => ({
      id: r.id,
      accountNumber: r.accountNumber,
      accountName: r.accountName,
      unitNumber: r.unitNumber,
      unitName: r.unitName,
    }));

    this.selectionChange.emit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['includeType'] && this.includeTypeControl) {
      this.includeTypeControl.setValue(this.includeType, { emitEvent: false });
    }
  }

  getCriteria(): FilterCriteria[] {
    if (this.selectedAccounts.length === 0) return [];

    const isExclude = this.includeTypeControl.value === 'exclude';
    const operator = isExclude ? 'NOTEQUALTO' : 'EQUALS';

    const unitLevelAccounts = this.selectedAccounts.filter(
      (item) => item.unitNumber && item.unitNumber.trim() !== '',
    );
    const accountLevelAccounts = this.selectedAccounts.filter(
      (item) => !item.unitNumber || item.unitNumber.trim() === '',
    );

    const unitCriteria: FilterCriteria[] = unitLevelAccounts.length
      ? [
          {
            tableAlias: 'claim',
            column: 'ACCOUNT_AND_UNIT',
            displayName: '',
            operator,
            values: unitLevelAccounts.map(
              (item) => `${item.accountNumber}|${item.unitNumber}`,
            ),
            lineOfBusiness: '',
            dataType: 'VARCHAR2',
            isClaimLab: false,
            formattedOperator: '',
            formattedValue: '',
            filterSection: 'Location',
            filterSubsection: 'Accounts/Units',
          } as FilterCriteria,
        ]
      : [];

    const accountCriteria: FilterCriteria[] = accountLevelAccounts.length
      ? [
          {
            tableAlias: 'claim',
            column: 'ACCOUNT_ONLY',
            displayName: '',
            operator,
            values: accountLevelAccounts.map(
              (item) => item.accountNumber || '',
            ),
            lineOfBusiness: '',
            dataType: 'VARCHAR2',
            isClaimLab: false,
            formattedOperator: '',
            formattedValue: '',
            filterSection: 'Location',
            filterSubsection: 'Accounts/Units',
          } as FilterCriteria,
        ]
      : [];

    return [...unitCriteria, ...accountCriteria];
  }

  getCount(): number {
    return this.selectedAccounts.length;
  }

  clearSelection(): void {
    this.selectedAccounts = [];
  }

  onAccountsChanged(accounts: AccountUnit[]) {
    this.selectedAccounts = accounts;
    this.selectionChange.emit();
  }

  onAccountSearchSelectionChanged(hasSelection: boolean) {
    this.hasAccountSearchSelection = hasSelection;
    this.accountSearchSelectionCount = this.searchAccountsTable
      ? this.searchAccountsTable.getSelectedItems().length
      : 0;
  }

  openAccountSearchDialog() {
    this.accountDialogVisible = true;
    this.accountSearchResults = [];
    this.hasAccountSearchSelection = false;
    this.accountSearchSelectionCount = 0;
    this.clearAccountSearch();
  }

  closeAccountDialog() {
    this.accountDialogVisible = false;
    this.accountSearchResults = [];
    this.hasAccountSearchSelection = false;
    this.accountSearchSelectionCount = 0;
  }

  clearAccountSearch() {
    this.accountSearchForm.reset({ acctLevel: this.searchLevelPreference });
    this.accountSearchResults = [];
    this.hasAccountSearchSelection = false;
    this.accountSearchSelectionCount = 0;
  }

  async searchAccounts() {
    if (this.contractNumber <= 0) return;

    this.searchingAccounts = true;
    this.hasAccountSearchSelection = false;
    this.accountSearchSelectionCount = 0;

    const response = await this.api.filtersCriteria.searchAccountUnits({
      contractNumber: this.contractNumber,
      acctLevel: this.accountSearchForm.controls.acctLevel.value === 'account',
      accountSearch:
        this.accountSearchForm.controls.accountSearch.value || undefined,
      unitSearch: this.accountSearchForm.controls.unitSearch.value || undefined,
      address: this.accountSearchForm.controls.address.value || undefined,
      city: this.accountSearchForm.controls.city.value || undefined,
      state: this.accountSearchForm.controls.state.value || undefined,
      zip: this.accountSearchForm.controls.zip.value || undefined,
    });

    const selectedIds = new Set(this.selectedAccounts.map((a) => a.id));
    this.accountSearchResults = response.accountUnits.filter(
      (r) => !selectedIds.has(r.id),
    );
    this.searchingAccounts = false;
  }

  addSelectedAccounts() {
    if (!this.searchAccountsTable) return;

    const selectedItems = this.searchAccountsTable.getSelectedItems();
    if (selectedItems.length === 0) return;

    const existingIds = new Set(this.selectedAccounts.map((a) => a.id));

    for (const item of selectedItems) {
      if (!existingIds.has(item.id)) {
        this.selectedAccounts.push({ ...item });
        existingIds.add(item.id);
      }
    }

    this.selectedAccounts = [...this.selectedAccounts];

    const addedIds = new Set(selectedItems.map((a) => a.id));
    this.accountSearchResults = this.accountSearchResults.filter(
      (r) => !addedIds.has(r.id),
    );

    this.searchAccountsTable.clearSelection();
    this.hasAccountSearchSelection = false;
    this.accountSearchSelectionCount = 0;
    this.selectionChange.emit();
  }

  async loadSearchLevelPreference() {
    const prefs = await this.api.reportHelpers.getReportPreferences(true);
    const accountSearch = prefs?.accountSearch;
    this.searchLevelPreference = accountSearch === 'A' ? 'account' : 'unit';
    this.accountSearchForm.controls.acctLevel.setValue(
      this.searchLevelPreference,
    );
  }
}
